import { SharedCard, SharedSwitch } from '@components';
import { TenantBuilds, TenantProfileService } from '@services';
import { Space, Typography, message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

const { Text, Title } = Typography;

interface IProps {
  tenantId: number;
}

/**
 * Host-only control for letting a garage use Builds (the Builds page, build creation and templates).
 *
 * Switching it off only hides them: existing builds and templates are kept, and come back as they were.
 */
export const BuildsAccessCard: React.FC<IProps> = ({ tenantId }) => {
  const [builds, setBuilds] = useState<TenantBuilds | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    TenantProfileService.getBuilds(tenantId)
      .then(setBuilds)
      .catch(() => {
        // The http interceptor already surfaces the failure; swallow it so a rejected promise in an
        // effect does not take the rest of the tenant page down with it.
      });
  }, [tenantId]);

  const handleToggle = useCallback(async () => {
    if (!builds) return;

    setSaving(true);
    try {
      const result = await TenantProfileService.setBuilds({ tenantId, isEnabled: !builds.isEnabled });
      setBuilds(result);
      message.success({ content: result.isEnabled ? 'Builds enabled' : 'Builds disabled' });
    } catch {
      // Surfaced by the http interceptor; caught here because this is wired straight to onChange.
    } finally {
      setSaving(false);
    }
  }, [tenantId, builds]);

  if (!builds) return null;

  return (
    <SharedCard style={{ boxShadow: 'none' }}>
      <Title level={5}>Builds</Title>

      <Space>
        <SharedSwitch active={builds.isEnabled} loading={saving} onChange={handleToggle} />
        <Text>{builds.isEnabled ? 'Builds enabled' : 'Builds disabled'}</Text>
      </Space>

      <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
        Shows the Builds page, the option to create a build, and My Templates. Turning it off only hides
        them: existing builds and templates are kept.
      </Text>
    </SharedCard>
  );
};
